import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import SearchableSelect from "../components/SearchableSelect";
import Header from "../components/Header";
import { getPartDetailsApi, savePrintLabelApi, savePrintLabelNewApi, getLabelPrintApi, getAddlTxt2Api, getTemplatesApi, getCompanyByShortCodeApi } from "../Api/api";
import { useParts } from "../context/PartsContext";

// Function to load PRN template from file
const loadTemplate = async (templateName) => {
  try {
    const response = await fetch(`/prn_templates/${templateName}.txt`);
    if (!response.ok) {
      throw new Error(`Failed to load template: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    console.error("Error loading template:", error);
    return null;
  }
};

// Build preview by repeating only the first-row template sections batch-by-batch.
const generateDynamicTemplate = (baseTemplate, labelCount, labelsPerRow = 4, setupTxt = "") => {
  const normalizedTemplate = (baseTemplate || "").replace(/\r\n/g, "\n");
  const safeLabelsPerRow = Math.max(1, parseInt(labelsPerRow, 10) || 4);

  const p1Index = normalizedTemplate.indexOf("P1");
  const beforeP1 = p1Index !== -1 ? normalizedTemplate.substring(0, p1Index) : normalizedTemplate;

  const labelStart =
    beforeP1.indexOf("; --- Label 1 ---") !== -1
      ? beforeP1.indexOf("; --- Label 1 ---")
      : beforeP1.search(/(?:^|\n)b\d+,\d+,Q,m2,s3,eL,iA,o6,/m);

  if (labelStart === -1) {
    return normalizedTemplate;
  }

  const fileSetupBlock = beforeP1.substring(0, labelStart).replace(/\s*$/, "");
  const templateBody = beforeP1.substring(labelStart).trim();
  const dbSetupBlock = (setupTxt || "").replace(/\r\n/g, "\n").trim();
  const effectiveSetupBlock = dbSetupBlock || fileSetupBlock || "N";

  const slotSections = [];

  // Preferred path: marker-based sections (; --- Label 1 ---).
  if (templateBody.includes("; --- Label 1 ---")) {
    for (let slot = 1; slot <= safeLabelsPerRow; slot++) {
      const startMarker = `; --- Label ${slot} ---`;
      const endMarker = `; --- Label ${slot + 1} ---`;
      const start = templateBody.indexOf(startMarker);

      if (start === -1) {
        continue;
      }

      const end = templateBody.indexOf(endMarker, start + startMarker.length);
      const section = end === -1 ? templateBody.substring(start).trim() : templateBody.substring(start, end).trim();
      slotSections.push({ slot, section });
    }
  } else {
    // Fallback path: unmarked templates (split by QR command lines).
    const lines = templateBody.split("\n");
    let currentSection = [];

    for (const line of lines) {
      const trimmed = line.trim();
      const isQrStart = /^b\d+,\d+,Q,m2,s3,eL,iA,o6,/.test(trimmed);

      if (isQrStart) {
        if (currentSection.length > 0) {
          const section = currentSection.join("\n").trim();
          if (section) {
            slotSections.push({ slot: slotSections.length + 1, section });
          }
        }
        currentSection = [line];
      } else if (currentSection.length > 0) {
        currentSection.push(line);
      }
    }

    if (currentSection.length > 0) {
      const section = currentSection.join("\n").trim();
      if (section) {
        slotSections.push({ slot: slotSections.length + 1, section });
      }
    }
  }

  if (slotSections.length === 0) {
    return normalizedTemplate;
  }

  const totalBatches = Math.ceil(labelCount / safeLabelsPerRow);
  const output = [];

  for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
    output.push(effectiveSetupBlock);
    output.push("");

    for (const { slot, section } of slotSections) {
      const globalIndex = batchIndex * safeLabelsPerRow + slot;
      if (globalIndex > labelCount) {
        continue;
      }

      const remappedSection = section.replace(/\{\{([A-Z0-9_]+)_(\d+)\}\}/g, (full, key, idx) => {
        return parseInt(idx, 10) === slot ? `{{${key}_${globalIndex}}}` : full;
      });

      output.push(remappedSection);
      output.push("");
    }

    // End-of-batch page break only between batches.
    if (batchIndex < totalBatches - 1) {
      output.push("P1");
      output.push("N");
    }
  }

  output.push("P1");
  output.push("N");

  return output.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
};

// Function to bind data to template variables
const bindTemplateData = (templateContent, data) => {
  if (!templateContent || !data) return "";
  
  let boundContent = templateContent;
  
  console.log("Binding data keys:", Object.keys(data));
  console.log("Template content snippet:", templateContent.substring(0, 100));
  
  // Generate QRDATA fields if they don't exist in the response
  for (let i = 1; i <= 12; i++) {
    const qrKey = `QRDATA_${i}`;
    if (!data[qrKey]) {
      // Generate QR data from other fields.
      const partNo = data[`PART_NO_${i}`] || "";
      const prodNo = data[`PROD_NO_${i}`] || "";
      const vcode = data[`VCODE_${i}`] || "";
      const addlTxt = data[`ADDL_TXT_${i}`] || "";
      data[qrKey] = `${partNo}$${prodNo}$${vcode}$${addlTxt}`.trim();
    } else {
      // Normalize API values to '$' delimiter for QR payload.
      data[qrKey] = String(data[qrKey]).replace(/\|/g, "$");
    }
  }
  
  // Replace all {{variable}} placeholders with data values
  Object.keys(data).forEach(key => {
    const placeholder = `{{${key}}}`;
    const value = data[key] !== null && data[key] !== undefined ? data[key] : "";
    console.log(`Trying to replace ${placeholder} with ${value}`);
    
    // Check if the placeholder exists in the template
    if (templateContent.includes(placeholder)) {
      console.log(`Found placeholder ${placeholder}`);
      boundContent = boundContent.replace(new RegExp(placeholder, 'g'), value);
    }
  });
  
  return boundContent;
};

function Dashboard() {
  // ===== STATES =====
  const [printer, setPrinter] = useState("");
  const [template, setTemplate] = useState("");
  const [vendor, setVendor] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [shortCode1, setShortCode1] = useState("SBC");
  const [printDate, setPrintDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [partNo, setPartNo] = useState("");
  const [partDesc, setPartDesc] = useState("");
  const [currentProdNo, setCurrentProdNo] = useState("");
  const [batchCode, setBatchCode] = useState("");
  const [heatCode, setHeatCode] = useState("");
  const [materialCode, setMaterialCode] = useState("");
  const [drawingLevel, setDrawingLevel] = useState("");
  const [userText1, setUserText1] = useState("");
  const [userText2, setUserText2] = useState("");
  const [addlText1, setAddlText1] = useState("");
  const [addlText2, setAddlText2] = useState("");
  const [vcode, setVcode] = useState("");
  const [vcodeList, setVcodeList] = useState([]);
  const [totalLabels, setTotalLabels] = useState(1);
  const [isCopied, setIsCopied] = useState(false);
  const { partsList } = useParts();

  // ===== LOAD COMPANY DATA FROM LOGIN =====
  const [companyData, setCompanyData] = useState(null);

 useEffect(() => {
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");

  if (userData.ComShortCode) {
    getCompanyByShortCodeApi(userData.ComShortCode)
      .then(response => {
        if (response.status === "success" && response.data) {

          const company = response.data;

          setCompanyData(company);

          setShortCode(company.ComShortCode);
          //setShortCode1(company.ComShortCode);

          // ⭐ SET DEFAULT VENDOR HERE
          setVendor(company.ComID.toString());
        }
      })
      .catch(error => {
        console.error("Error fetching company:", error);
      });
  }
}, []);


  // ===== FETCH PARTS FROM API =====
  useEffect(() => {
    // Get company short code from localStorage
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const comShortCode = userData.ComShortCode || "";
    
    console.log("userData from localStorage:", userData);
    console.log("comShortCode:", comShortCode);
    
    getPartDetailsApi(comShortCode)
      .then(data => {
        console.log("getPartDetailsApi response:", data);
        if (data && data.data) {
          // Handle new API response format
          if (Array.isArray(data.data[0])) {
            // New format: data.data[0] is array of parts, data.data[1] is VCode array
            setPartList(data.data[0]);
            
            // Extract VCode options from data[1]
            const vcodeData = data.data[1] || [];
            const vcodeOptions = vcodeData
              .filter(item => item && item.Vcode !== null && item.Vcode !== undefined)
              .map(item => ({ value: item.Vcode, label: item.Vcode }));
            setVcodeList(vcodeOptions);
          } else {
            // Original format
            setPartList(data.data);
          }
        }
      })
      .catch(err => console.error("Error fetching parts:", err));

    // Fetch Additional Text 2 options
    getAddlTxt2Api(comShortCode)
      .then(data => {
        console.log("getAddlTxt2Api response:", data);
        if (data && data.data) {
          // Handle new API response format - data.data is an array
          let validOptions = [];
          
          if (Array.isArray(data.data[0])) {
            // New format: data.data[0] is array of objects with ListData
            validOptions = data.data[0]
              .filter(item => item && item.ListData) // Filter out null/undefined/empty
              .map(item => item.ListData);
          } else {
            // Original format
            validOptions = data.data
              .filter(item => item.ListData)
              .map(item => item.ListData);
          }
          
          setAddlText2List(validOptions);
        }
      })
      .catch(err => console.error("Error fetching Addl Text 2 options:", err));

    // Fetch Templates
    getTemplatesApi(comShortCode)
      .then(data => {
        console.log("getTemplatesApi response:", data);
        if (data && data.data) {
          // Handle new API response format
          let templateData;
          if (Array.isArray(data.data[0])) {
            templateData = data.data[0];
          } else {
            templateData = data.data;
          }
          
          setTemplateList(templateData);
          // Auto-select first template when templates are loaded
          if (templateData.length > 0) {
            const firstTemplate = templateData[0];
            setTemplate(firstTemplate.TmpName);
            if (firstTemplate.LabelsPerRow) {
              setTotalLabels(firstTemplate.LabelsPerRow);
            }
          }
        }
      })
      .catch(err => console.error("Error fetching templates:", err));

    // Auto-select first printer from static list
    if (printerList.length > 0) {
      setPrinter(printerList[0]);
    }
  }, []);

  // ===== SAMPLE DATA (Replace with API Data) =====
  const printerList = ["TVSELP46", "TVSELP47"];
  
  // Vendor list - use company data from login if available
const getVendorList = () => {
  if (companyData) {
    return [
      {
        id: companyData.ComID,
        name: companyData.ComName,
        short: companyData.ComShortCode
      }
    ];
  }

  return [];
};
  
  const vendorList = getVendorList();
  const [partList, setPartList] = useState([]);
  const [addlText2List, setAddlText2List] = useState([]);
  const [templateList, setTemplateList] = useState([]);

  const handleTemplateChange = (templateName) => {
    setTemplate(templateName);
    const selectedTemplate = templateList.find(t => t.TmpName === templateName);
    if (selectedTemplate?.LabelsPerRow) {
      setTotalLabels(selectedTemplate.LabelsPerRow);
    }
  };

  // ===== HANDLE VENDOR CHANGE =====
  const handleVendorChange = async (id) => {
    setVendor(id);
    // Find the selected vendor
    const selected = vendorList.find(v => v.id === parseInt(id));
    if (selected) {
      try {
        // Call API to get company details by short code
        const response = await getCompanyByShortCodeApi(selected.short);
        if (response.status === "success" && response.data) {
          // Update company data and short code
          setCompanyData(response.data);
          setShortCode(response.data.ComShortCode);
         // setShortCode1(response.data.ComShortCode); // Update Short Code1 with new value
        }
      } catch (error) {
        console.error("Error fetching company details:", error);
        // Fallback to vendor list data if API fails
        setShortCode(selected.short);
        setShortCode1(selected.short); // Update Short Code1 with new value
      }
    }
  };

  // ===== HANDLE PART CHANGE =====
  const handlePartChange = (partNoValue) => {
    setPartNo(partNoValue);
    const selected = partList.find(p => p.partNo === partNoValue);
    if (selected) {
      setPartDesc(selected.PartDesc || "");
      setCurrentProdNo(selected.CurrentProdNo?.toString() || "");
      setBatchCode(selected.BatchCode || "");
      setHeatCode(selected.HeatCode || "");
      setMaterialCode(selected.MaterialCode || "");
      setDrawingLevel(selected.Drawinglevel || "");
      setAddlText2(selected.AddlTxt2 || "");
      setVcode(selected.Vcode || "");
    }
  };

  const refreshCurrentProdNo = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const comShortCode = companyData?.ComShortCode || userData.ComShortCode || "";
      if (!comShortCode || !partNo) return;

      const response = await getPartDetailsApi(comShortCode);
      if (response?.status === "success" && response.data) {
        // Handle new API response format
        let partsArray;
        if (Array.isArray(response.data[0])) {
          partsArray = response.data[0];
        } else {
          partsArray = response.data;
        }
        
        const selectedPart = partsArray.find(
          p => String(p.partNo || "").trim().toLowerCase() === String(partNo).trim().toLowerCase()
        );

        if (selectedPart) {
          setCurrentProdNo(selectedPart.CurrentProdNo?.toString() || "");
        }
      }
    } catch (error) {
      console.error("Error refreshing CurrentProdNo:", error);
    }
  };

  // ===== HANDLE BILL GENERATION AND PRINT LABELS =====
  const [billsGenerated, setBillsGenerated] = useState(false);
  const [lblCnt, setLblCnt] = useState(0);
  const [generateBillsLoading, setGenerateBillsLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printMessage, setPrintMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [boundTemplateData, setBoundTemplateData] = useState("");
  const [popupPosition, setPopupPosition] = useState({ x: 120, y: 80 });
  const popupDragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

  useEffect(() => {
    if (showPopup) {
      const initialX = Math.max(20, Math.floor((window.innerWidth - 800) / 2));
      setPopupPosition({ x: initialX, y: 60 });
    }
  }, [showPopup]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!popupDragRef.current.dragging) return;

      const panelWidth = 800;
      const panelHeight = 640;
      const x = e.clientX - popupDragRef.current.offsetX;
      const y = e.clientY - popupDragRef.current.offsetY;

      const clampedX = Math.min(Math.max(20, x), Math.max(20, window.innerWidth - panelWidth - 20));
      const clampedY = Math.min(Math.max(20, y), Math.max(20, window.innerHeight - panelHeight - 20));
      setPopupPosition({ x: clampedX, y: clampedY });
    };

    const handleMouseUp = () => {
      popupDragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const startPopupDrag = (e) => {
    if (e.button !== 0) return;
    popupDragRef.current.dragging = true;
    popupDragRef.current.offsetX = e.clientX - popupPosition.x;
    popupDragRef.current.offsetY = e.clientY - popupPosition.y;
    e.preventDefault();
  };

  const handlePrintLabels = async () => {
    // Validate required fields
    if (!vendor || !shortCode) {
      setPrintMessage("Please select Vendor");
      return;
    }

    setPrintLoading(true);
    setPrintMessage("");

    try {
      // Get LogicProcedure from selected template
      const selectedTemplate = templateList.find(t => t.TmpName === template);
      const procName = selectedTemplate?.LogicProcedure;

      // Call the new API to get label print data
      const response = await getLabelPrintApi(shortCode, procName);

      if (response.status === "success" && response.data && response.data.length > 0) {
        setPrintMessage("Labels retrieved successfully");
        
        // Load the base template
        let templateContent = await loadTemplate(template);
        
        if (templateContent) {
          const selectedTemplate = templateList.find(t => t.TmpName === template);
          const labelsPerRow = selectedTemplate?.LabelsPerRow || 1;
          const setupTxt = selectedTemplate?.SetupTxt || "";
          
          let fullPrintContent = setupTxt + "\n";
          
          // Process each label in batches
          for (let batchStartIndex = 0; batchStartIndex < response.data.length; batchStartIndex += labelsPerRow) {
            let rowPrn = templateContent;
            
            for (let slot = 1; slot <= labelsPerRow; slot++) {
              const currentLabelIdx = batchStartIndex + (slot - 1);
              
              if (currentLabelIdx < response.data.length) {
                const labelData = response.data[currentLabelIdx];
                
                // Replace placeholders in the template with actual data
                for (let key in labelData) {
                  const placeholder = `{{${key}_${slot}}}`;
                  const value = labelData[key] !== null && labelData[key] !== undefined ? String(labelData[key]) : "";
                  rowPrn = rowPrn.replace(new RegExp(placeholder, 'g'), value);
                }
              }
            }
            
            // Add batch content to full print content
            fullPrintContent += "N\n" + rowPrn + "\nP1\n";
          }
          
          setBoundTemplateData(fullPrintContent);
          setShowPopup(true);
        }
      } else {
        setPrintMessage(response.message || "Failed to retrieve labels");
      }
    } catch (error) {
      setPrintMessage("Error retrieving labels. Please try again.");
      console.error("Get label print error:", error);
    } finally {
      setPrintLoading(false);
      await refreshPartsAndProdNo();
    }
  };

 const refreshPartsAndProdNo = async () => {
  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const comShortCode =
      companyData?.ComShortCode || userData.ComShortCode || "";

    if (!comShortCode) return;

    const response = await getPartDetailsApi(comShortCode);

    if (response?.status === "success" && response.data) {
      let partsArray;

      if (Array.isArray(response.data[0])) {
        partsArray = response.data[0];
      } else {
        partsArray = response.data;
      }

      // 🔥 ONLY update part list
      setPartList(partsArray);
    }
  } catch (error) {
    console.error("Error refreshing parts:", error);
  }
};


  // ===== HANDLE BILL GENERATION =====
  const handleGenerateBills = async () => {
    // Validate required fields
    if (!vendor || !shortCode || !partNo || !template) {
      setPrintMessage("Please select Vendor, Part and Template");
      return;
    }

    setGenerateBillsLoading(true);
    setPrintMessage("");

    try {
      // Build the payload
      const payload = {
        ProductionNo: parseInt(currentProdNo) || 1,
        LabelQty: parseInt(totalLabels) || 1,
        UserTxt1: userText1 || "",
        UserTxt2: userText2 || "",
        Vcode: vcode || "",
        ComShortCode: companyData?.ComShortCode || JSON.parse(localStorage.getItem("userData") || "{}").ComShortCode || "",
        ComShortCode1: shortCode1 || "",
        PrdDate: printDate || "",
        PartNo: partNo || "",
        PartDesc: partDesc || "",
        AddlTxt1: addlText1 || "",
        AddlTxt2: addlText2 || "",
        TemplateName: template || "",
        BatchCode: batchCode || "",
        HeatCode: heatCode || "",
        MaterialCode: materialCode || "",
        Drawinglevel: drawingLevel || ""
      };

      const response = await savePrintLabelNewApi(payload);

      if (response.status === "success" && response.data && response.data.length > 0) {
        const lblCount = response.data[0].LblCnt || 0;
        setLblCnt(lblCount);
        setBillsGenerated(true);
        setPrintMessage("Bills generated successfully!");
      } else {
        setPrintMessage(response.message || "Failed to generate bills");
      }
    } catch (error) {
      setPrintMessage("Error generating bills. Please try again.");
      console.error("Generate bills error:", error);
    } finally {
      setGenerateBillsLoading(false);
    }
  };

  const handleReset = () => {
    setPrinter("");
    //setTemplate("");
   // setVendor("");
    //setShortCode("");
   // setShortCode1("");
    // setPrintDate(() => {
    //   const today = new Date();
    //   return today.toISOString().split('T')[0];
    // });
    setPartNo("");
    setPartDesc("");
     setCurrentProdNo("");
    setBatchCode("");
    setHeatCode("");
    setMaterialCode("");
    setDrawingLevel("");
    setUserText1("");
    setUserText2("");
    setAddlText1("");
    setAddlText2("");
    setVcode("");
   // setTotalLabels(1);
    setBillsGenerated(false);
    setLblCnt(0);
  };

  const clearLabelDataOnly = () => {
  setPartNo("");
  setPartDesc("");
  setCurrentProdNo("");
  setBatchCode("");
  setHeatCode("");
  setMaterialCode("");
  setDrawingLevel("");
  setUserText1("");
  setUserText2("");
  setAddlText1("");
  setAddlText2("");
  setVcode("");
  //setTotalLabels(1);
};
  // ===== STYLES =====
  const panelBox = {
    background: "#f5f7f9",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  };

  const panelTitle = {
    fontSize: "13px",
    fontWeight: 700,
    color: "#1f2937",
    borderBottom: "2px solid #3b82f6",
    paddingBottom: "10px",
    marginBottom: "18px",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  };

  const compactInput = {
    width: "100%",
    height: "38px",
    padding: "6px 12px",
    fontSize: "13px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    background: "#fff",
    marginBottom: "14px",
    boxSizing: "border-box"
  };

  const selectStyle = {
    ...compactInput,
    cursor: "pointer"
  };

  const orangeBtn = {
    width: "100%",
    background: "#e65100",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "12px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  };

  const greenBtn = {
    width: "100%",
    marginTop: "28px",
    background: "#1565c0",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "14px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "6px",
    display: "block"
  };

  const prodNoBox = {
    fontSize: "28px",
    fontWeight: 700,
    background: "#e8f5e9",
    color: "#2e7d32",
    padding: "12px",
    borderRadius: "6px",
    textAlign: "center",
    marginBottom: "16px"
  };

  // ===== HELPER FOR COLUMNS =====
  const col = (size) => ({ flex: `0 0 ${size}%`, maxWidth: `${size}%`, padding: "0 8px", boxSizing: "border-box" });
  const row = () => ({ display: "flex", flexWrap: "wrap", margin: "0 -8px" });

  return ( 
    <div style={{ background: "#e5e7eb", minHeight: "100vh" }}>

      <Sidebar currentPage="dashboard" />

      <Header title="PRODUCTION LABELING" />

      {/* CENTER MAIN BOX */}
      <div style={{
        maxWidth: "1300px",
        margin: "30px auto",
        background: "#fff",
        padding: "30px",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
      }}>

        <div style={row()}>

          {/* LEFT PARTITION */}
          <div style={{ ...col(33), marginBottom: "20px" }}>
            <div style={panelBox}>
              <div style={panelTitle}>SETTINGS</div>

              {/* <SearchableSelect
                value={printer}
                onChange={setPrinter}
                options={printerList}
                placeholder="Select Printer"
                label="Printer"
              /> */}

              <SearchableSelect
                value={template}
                onChange={handleTemplateChange}
                options={templateList}
                placeholder="Select Template"
                label="Template"
                displayKey="TmpName"
                valueKey="TmpName"
              />

              <SearchableSelect
                value={vendor}
                onChange={handleVendorChange}
                options={vendorList}
                placeholder="Select Vendor"
                label="Select UNIT"
                displayKey="name"
              />

              <div style={{ display: "flex", gap: "15px" }}>
                <div style={{ flex: 1 }}>
                  <div style={labelStyle}>UNIT Short Code</div>
                  <input style={{ ...compactInput, background: "#f3f4f6", color: "#6b7280" }} value={shortCode} readOnly />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={labelStyle}>UNIT Short Code1</div>
                  <input style={compactInput} value={shortCode1} onChange={e => setShortCode1(e.target.value)} placeholder="Enter Short Code1" />
                </div>
              </div>

              <div style={labelStyle}>Print Date</div>
              <input type="date" style={compactInput} value={printDate} onChange={e => setPrintDate(e.target.value)} />
            </div>


            <div style={{ ...panelBox, marginTop: "20px" }}>
              <div style={panelTitle}>TRANSACTION INFO</div>

              <div style={labelStyle}>NEXT PROD NO (START)</div>
              <div style={prodNoBox}>
                {currentProdNo || "-"}
              </div>

              <button style={orangeBtn} onClick={handleReset}>
                RESET FORM
              </button>
            </div>

          </div>

          {/* RIGHT PARTITION */}
          <div style={{ ...col(67), marginBottom: "20px" }}>

            <div style={panelBox}>
              <div style={panelTitle}>LABEL DATA</div>

              <SearchableSelect
                value={partNo}
                onChange={handlePartChange}
                options={partList}
                placeholder="Select Part"
                label="Search Part Number"
                displayKey="partNo"
                valueKey="partNo"
              />

              {/* VCode and AddlTxt2 Row */}
              <div style={row()}>
                <div style={{ ...col(50), padding: "0 8px" }}>
                  <div style={labelStyle}>Vendor Code</div>
                  <input
                    style={{ ...compactInput, background: "#f3f4f6", color: "#6b7280" }} 
                    value={vcode}
                    onChange={e => setVcode(e.target.value)}
                    placeholder="Vendor Code"
                    readOnly

                  />
                </div>

                <div style={{ ...col(50), padding: "0 8px" }}>
                  <div style={labelStyle}>Addl Text 2</div>
                  <input
                    style={{ ...compactInput, background: "#f3f4f6", color: "#6b7280" }} 
                    value={addlText2}
                    onChange={e => setAddlText2(e.target.value)}
                    placeholder="Addl Text 2"
                    readOnly
                  />
                </div>
              </div>

              {/* Part Description Row */}
              <div style={row()}>
                <div style={{ ...col(75), padding: "0 8px" }}>
                  <div style={labelStyle}>Part Description</div>
                  <input style={{ ...compactInput, background: "#f3f4f6", color: "#6b7280" }} value={partDesc} readOnly />
                </div>

               <div style={{ ...col(25), padding: "0 8px" }}>
  <div style={labelStyle}>Total Labels</div>
  <input
    type="text"
     style={{
      ...compactInput,
      textAlign: "right"  
    }}
    value={totalLabels}
    onChange={(e) => {
      const value = e.target.value;
      if (/^\d*$/.test(value)) {   // allow only digits
        setTotalLabels(value);
      }
    }}
    inputMode="numeric"
  />
</div>
              </div>

              {/* Additional Part Fields Row */}
              <div style={row()}>
                <div style={{ ...col(25), padding: "0 8px" }}>
                  <div style={labelStyle}>Batch Code</div>
                  <input
                    style={compactInput}
                    value={batchCode}
                    onChange={e => setBatchCode(e.target.value)}
                    placeholder="Batch Code"
                  />
                </div>

                <div style={{ ...col(25), padding: "0 8px" }}>
                  <div style={labelStyle}>Heat Code</div>
                  <input
                    style={compactInput}
                    value={heatCode}
                    onChange={e => setHeatCode(e.target.value)}
                    placeholder="Heat Code"
                  />
                </div>

                <div style={{ ...col(25), padding: "0 8px" }}>
                  <div style={labelStyle}>Material Code</div>
                  <input
                    style={compactInput}
                    value={materialCode}
                    onChange={e => setMaterialCode(e.target.value)}
                    placeholder="Material Code"
                  />
                </div>

                <div style={{ ...col(25), padding: "0 8px" }}>
                  <div style={labelStyle}>Drawing Level</div>
                  <input
                    style={{ ...compactInput, background: "#f3f4f6", color: "#6b7280" }} 
                    value={drawingLevel}
                    onChange={e => setDrawingLevel(e.target.value)}
                    placeholder="Drawing Level"
                    readOnly
                  />
                </div>
              </div>

              {/* User Fields Row */}
              <div style={row()}>
                <div style={{ ...col(85), padding: "0 8px" }}>
                  <div style={labelStyle}>User Text 1</div>
                  <input
                    style={compactInput}
                    value={userText1}
                    onChange={e => setUserText1(e.target.value)}
                    placeholder="Enter text..."
                  />
                </div>

               <div style={{ ...col(85), padding: "0 8px" }}>
                  <div style={labelStyle}>User Text 2</div>
                  <input
                    style={compactInput}
                    value={userText2}
                    onChange={e => setUserText2(e.target.value)}
                    placeholder="Enter text..."
                  />
                </div>

               <div style={{ ...col(75), padding: "0 8px" }}>
                  <div style={labelStyle}>Addl Text 1</div>
                  <input
                    style={compactInput}
                    value={addlText1}
                    onChange={e => setAddlText1(e.target.value)}
                    placeholder="Enter text..."
                  />
                </div>
              </div>

              {/* Print Labels (Batched) Button */}
              {!billsGenerated ? (
                <button 
                  style={{...greenBtn, ...(generateBillsLoading ? {background: "#9ca3af"} : {})}}
                  onClick={handleGenerateBills}
                  disabled={generateBillsLoading}
                >
                  {generateBillsLoading ? "PRINTING LABELS..." : "PRINT LABELS (BATCHED)"}
                </button>
              ) : (
                <div>
                  {/* Display LblCnt */}
                  <div style={{
                    marginTop: "12px",
                    padding: "10px",
                    borderRadius: "6px",
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: 600,
                    backgroundColor: "#dbeafe",
                    color: "#1e40af"
                  }}>
                    Labels Count: {lblCnt}
                  </div>
                  
                  {/* Show Labels Button */}
                  <button 
                    style={{...greenBtn, ...(printLoading ? {background: "#9ca3af"} : {})}}
                    onClick={handlePrintLabels}
                    disabled={printLoading}
                  >
                    {printLoading ? "LOADING..." : "SHOW LABELS"}
                  </button>
                </div>
              )}
              
              {printMessage && (
                <div style={{
                  marginTop: "12px",
                  padding: "10px",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontSize: "13px",
                  backgroundColor: printMessage.includes("success") ? "#d1fae5" : "#fee2e2",
                  color: printMessage.includes("success") ? "#065f46" : "#991b1b"
                }}>
                  {printMessage}
                </div>
              )}
            </div>

          </div>

        </div>

      </div>

      {/* Label Preview Popup */}
      {showPopup && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000
        }}>
          <div style={{
            position: "fixed",
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            width: "min(800px, calc(100vw - 40px))",
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "24px",
            maxHeight: "70vh",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
          }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1px",
              cursor: "move",
              userSelect: "none"
            }}
            onMouseDown={startPopupDrag}
          >
            <h2 style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#1f2937",
              margin: 0
            }}>
              Label Preview - {template}
            </h2>

            {/* Right Side Buttons */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(boundTemplateData);
                    setIsCopied(true);   // ✅ ENABLE BUTTON
                    alert("Label content copied!");
                  } catch (error) {
                    alert("Copy failed");
                  }
                }}
                style={{
                  background: "#10b981",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer"
                }}
              >
                Copy
              </button>
               <button
  onClick={async () => {
    try {
      const res = await fetch("http://localhost:5050/rmpprintdesk", {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: boundTemplateData
      });

      if (!res.ok) {
        throw new Error("Server error");
      }

      const msg = await res.text();
      alert(msg || "Printed successfully");
    } catch (err) {
      console.error("PRINT ERROR:", err);

      // ✅ DIFFERENT HANDLING
      if (err instanceof TypeError) {
        // 🔥 This happens when EXE not running / network issue
        alert("⚠️ Run the EXE (Print Service not running)");
      } else {
        // Server responded but error (like 400)
        alert("❌ Printer error / configuration issue");
      }
    }
  }}
  style={{
    background: "#2563eb",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer"
  }}
>
  🖨️ Print Label
</button>

              {/* <button
                onClick={() => setShowPopup(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "22px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  color: "#6b7280"
                }}
              >
                ×
              </button> */}
            </div>
          </div>
            <div style={{
              flex: 1,
              minHeight: "220px",
              backgroundColor: "#f3f4f6",
              padding: "16px",
              borderRadius: "6px",
              fontFamily: "monospace",
              fontSize: "12px",
              whiteSpace: "pre-wrap",
              overflowY: "auto",
              overflowX: "auto"
            }}>
              {boundTemplateData}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "4px"
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >

                <div
  style={{
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px"   // 🔥 space between buttons
  }}
>


  <button
    onClick={() => {
      setShowPopup(false);
      setBillsGenerated(false);
      setLblCnt(0);
      clearLabelDataOnly();
      setIsCopied(false);
    }}
    style={{
      background: "#2563eb",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      padding: "6px 14px",
      fontSize: "13px",
      fontWeight: 500,
      cursor: "pointer"
    }}
  >
    Printing Completed
  </button>
</div>
            
            </div>
          </div>
        </div>
      )}

    </div>

  );   
}

export default Dashboard;

